import React, { useCallback } from 'react';
import { Column } from 'react-table';
import { StylesConfig } from 'react-select';
import cn from 'classnames';
import { GenericDropdownOption } from '../../../../App.types';
import Table, { RenderFooterProps, TableProps } from '../Table/Table';
import LeftArrowIcon from '../../../../SharedComponents/svg/Icons/LeftArrowIcon';
import RightArrowIcon from '../../../../SharedComponents/svg/Icons/RightArrowIcon';
import { OrderByParam } from '../../../../Hooks/useReactTableSortToOrderBy';
import Select from '../../../DataEntry/Select/Select';

import styles from './PaginationTable.module.css';

const defaultPageSizeOptions: GenericDropdownOption<number>[] = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '35', value: 35 },
    { label: '50', value: 50 },
];

const pageSizeSelectStyles: StylesConfig = {
    valueContainer: (base) => ({
        ...base,
        minWidth: '2rem',
        fontWeight: 500,
    }),
    menu: (base) => ({
        ...base,
        fontSize: '0.75rem',
        fontWeight: 500,
    }),
};

export type PaginationTableProps<T extends object> = {
    loading: boolean;
    noDataLabel?: string;
    data: T[];
    columns: Column<T>[];
    count: number;
    page: number;
    setPage: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number | undefined) => void;
    pageSizeOptions?: GenericDropdownOption<number>[];
    onRowClick?: (item: T) => void;
    manualPagination?: boolean;
    autoResetPage?: boolean;
    disableSortBy?: boolean;
    orderBy?: OrderByParam<T>;
    onOrderByChange?: (param: OrderByParam<T>) => void;
    manualSortBy?: boolean;
    tbodyClassName?: string;
    theadClassName?: string;
    tableClassName?: string;
} & Pick<TableProps<T>, 'onPaginationChange'>;

const PaginationTable = <T extends object>({
    loading,
    noDataLabel,
    data,
    columns,
    count,
    page,
    setPage,
    pageSize,
    setPageSize,
    pageSizeOptions = defaultPageSizeOptions,
    onRowClick,
    manualPagination = true,
    autoResetPage,
    disableSortBy = true,
    onOrderByChange,
    orderBy,
    manualSortBy,
    tbodyClassName,
    theadClassName,
    tableClassName,
    onPaginationChange,
}: PaginationTableProps<T>) => {
    const renderFooter = useCallback(
        ({
            canPreviousPage,
            canNextPage,
            goNext,
            goPrev,
            setPageSize: tableSetPageSize,
            page,
            pageCount,
        }: RenderFooterProps) => (
            <div className={styles.tfoot}>
                <div className={styles.pageSizeSelectContainer} data-test-id="page-size-select-container">
                    <Select
                        isSearchable={false}
                        isClearable={false}
                        styles={pageSizeSelectStyles}
                        options={pageSizeOptions}
                        value={pageSizeOptions.find((o) => o.value === pageSize)}
                        onChange={(pageSizeOption) => {
                            if (!manualPagination) {
                                tableSetPageSize(pageSizeOption?.value ?? 10);
                            }

                            setPageSize(pageSizeOption?.value);
                        }}
                        menuPosition="fixed"
                    />
                    <label>Records per page</label>
                </div>

                <div className={styles.pagination}>
                    {pageCount > 0 && (
                        <span className={styles.pageLocationLabel}>
                            Page {page} of {pageCount}
                        </span>
                    )}

                    <LeftArrowIcon
                        ariaRole="button"
                        ariaLabel="Go to previous page"
                        className={cn(styles.paginationIcon, {
                            [styles.paginationIconDisabled]: !canPreviousPage || loading,
                        })}
                        width={12}
                        height={12}
                        onClick={goPrev}
                    />

                    <RightArrowIcon
                        ariaRole="button"
                        ariaLabel="Go to next page"
                        className={cn(styles.paginationIcon, {
                            [styles.paginationIconDisabled]: !canNextPage || loading,
                        })}
                        height={12}
                        width={12}
                        onClick={goNext}
                    />
                </div>
            </div>
        ),
        [loading, manualPagination, pageSize, pageSizeOptions, setPageSize]
    );

    // skeletons are rendered in table cells,
    // so we need some rows to display skeletons on initial loading
    const initialLoading = !data.length && loading;
    const emptyRows = new Array(pageSize || 0).fill({}) as T[];
    const rows = initialLoading ? emptyRows : data;

    return (
        <Table
            disableSortBy={disableSortBy}
            manualSortBy={manualSortBy}
            manualPagination={manualPagination}
            autoResetPage={autoResetPage}
            data={rows}
            columns={columns}
            noDataLabel={noDataLabel}
            count={count}
            pageIndex={page - 1}
            pageSize={pageSize}
            onPageIndexChange={(pageIndex) => setPage(pageIndex + 1)}
            tableClassName={tableClassName}
            theadClassName={cn(styles.thead, theadClassName)}
            tbodyClassName={cn(styles.tbody, tbodyClassName)}
            renderFooter={renderFooter}
            onRowClick={onRowClick}
            orderBy={orderBy}
            onOrderByChange={onOrderByChange}
            onPaginationChange={onPaginationChange}
        />
    );
};

export default PaginationTable;
